import type { DirectoryEntityType } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { LOCATION_SEARCH_PARAM_KEYS } from '@modules/search/config';
import { SEARCH_LOCATION_NAME_LEVELS } from '~/modules/search/util/to-aggregation-search-location';

/**
 * Search-service sort used when the URL has no sort segment. Products and
 * projects stay on featured; professionals stay on trending until a location
 * is applied.
 */
export const DIRECTORY_DEFAULT_SORT: Record<
    DirectoryEntityType,
    'featured' | 'trending'
> = {
    products: 'featured',
    professionals: 'trending',
    projects: 'featured',
};

/**
 * Featured is the search-service sort that ranks by distance first within each
 * location tier. Used as the professionals directory default once a location
 * is applied, so the landing view matches nearest-first rather than view counts.
 */
export const PROFESSIONALS_LOCATION_DEFAULT_SORT = 'featured' as const;

/** Dropdown label for featured once a location makes it the default sort. */
export const PROFESSIONALS_LOCATION_SORT_TITLE = 'Closest';

const FEATURED_SORT_TITLE = 'Featured';

const LOCATION_PATH_SEGMENT = /\/location-[^/]+/;

/**
 * Default sort for a directory grid. A professionals location (path page or
 * filter-panel place) switches the default to featured so distance leads.
 */
export const getDirectoryDefaultSort = (
    entityType: DirectoryEntityType,
    hasLocation = false,
): 'featured' | 'trending' => {
    if (entityType === 'professionals' && hasLocation) {
        return PROFESSIONALS_LOCATION_DEFAULT_SORT;
    }
    return DIRECTORY_DEFAULT_SORT[entityType];
};

/**
 * True when a searchLocation object names a place. Coordinates alone never
 * create a location filter, so they do not count.
 */
export const searchLocationHasPlace = (location: unknown): boolean => {
    if (!location || typeof location !== 'object') {
        return false;
    }
    const record = location as Record<string, unknown>;
    return SEARCH_LOCATION_NAME_LEVELS.some((level) => Boolean(record[level]));
};

/**
 * True when `filters` carries a searchLocation used to rank the professionals
 * directory. View More and omitted-sort fallbacks use this to pick featured.
 */
export const filtersHaveSearchLocation = (
    filters?: { searchLocation?: unknown } | null,
): boolean => searchLocationHasPlace(filters?.searchLocation);

/**
 * True when the URL already carries a filter-panel place (city/region/…). Path
 * `location-{slug}` pages do not put those query params on the URL — callers
 * still pass the resolved location separately.
 */
export const urlHasSearchLocation = (url: string): boolean => {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
        return false;
    }
    const params = new URLSearchParams(url.slice(queryIndex));
    return Object.values(LOCATION_SEARCH_PARAM_KEYS).some((key) =>
        Boolean(params.get(key)),
    );
};

/**
 * True when a professionals directory URL has a place — either a filter-panel
 * query or a `location-{slug}` path segment.
 */
export const urlHasProfessionalsLocation = (url: string): boolean =>
    urlHasSearchLocation(url) ||
    LOCATION_PATH_SEGMENT.test(url.split('?')[0] ?? '');

/**
 * Label for the featured sort option. Closest only when this is the
 * professionals directory *and* a location is applied — other professional
 * surfaces (partners, journal) keep Featured even when they default to that sort.
 */
export const featuredSortTitle = (
    searchIndex: SearchIndexes | 'all' | null | undefined,
    hasLocation: boolean,
): string => {
    if (searchIndex === SearchIndexes.Professionals && hasLocation) {
        return PROFESSIONALS_LOCATION_SORT_TITLE;
    }
    return FEATURED_SORT_TITLE;
};

/**
 * Current and destination omitted-sort defaults for a professionals directory
 * URL. Other surfaces return undefined so callers keep their own default.
 */
export const getProfessionalsSortDefaults = (
    originalUrl: string,
    destSelectedLocation: unknown,
):
    | {
          currentDefault: 'featured' | 'trending';
          destinationDefault: 'featured' | 'trending';
      }
    | undefined => {
    const path = originalUrl.split('?')[0] ?? '';
    if (!path.startsWith('/professionals')) {
        return undefined;
    }
    return {
        currentDefault: getDirectoryDefaultSort(
            'professionals',
            urlHasProfessionalsLocation(originalUrl),
        ),
        destinationDefault: getDirectoryDefaultSort(
            'professionals',
            searchLocationHasPlace(destSelectedLocation) ||
                urlHasProfessionalsLocation(path),
        ),
    };
};

/**
 * Whether a sort belongs in the destination URL. Omits the destination default,
 * and does not freeze the current page's implicit default when the destination
 * default is about to change (applying a location would otherwise turn an
 * omitted trending into an explicit sort_trending, or drop an explicit one).
 */
export const shouldWriteSortParam = ({
    sortBy,
    currentDefault,
    destinationDefault,
    originalHadExplicitSort,
}: {
    sortBy?: string | null;
    currentDefault?: string | null;
    destinationDefault?: string | null;
    originalHadExplicitSort: boolean;
}): boolean => {
    if (!sortBy) {
        return false;
    }
    if (sortBy === destinationDefault) {
        return false;
    }
    if (!originalHadExplicitSort && sortBy === currentDefault) {
        return false;
    }
    return true;
};
